
import { supabase } from './supabase';
import { User, UserRole } from '../types';
import { debug, mapUser, mapSessionUser, logAuthStep } from './auth_utils';

export async function fetchUserProfile(userId: string): Promise<User | null> {
      try {
        const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', userId)
            .single();
        
        if (error) return null;
        return data ? mapUser(data) : null;
      } catch (e) {
          return null;
      }
}

export async function ensureUserProfile(authUser: any): Promise<User | null> {
    const { data: existing } = await supabase.from('profiles').select('id').eq('id', authUser.id).maybeSingle();
    if (existing) return fetchUserProfile(authUser.id);

    debug('REPAIR', `Återskapar saknad profil för ${authUser.email}`);
    const meta = authUser.user_metadata || {};
    
    const name = meta.name || authUser.email?.split('@')[0] || 'Användare';
    const role = meta.role || UserRole.TEACHER;
    
    const { data, error } = await supabase.from('profiles').insert([{
        id: authUser.id,
        email: authUser.email,
        name: name,
        role: role,
        status: 'PENDING' 
    }]).select().single();

    if (error) {
        console.error("Kunde inte återskapa profil:", error);
        const fallback = mapSessionUser(authUser);
        fallback.status = 'PENDING'; 
        return fallback;
    }
    
    return data ? mapUser(data) : mapSessionUser(authUser);
}

/**
 * Server-Side Login via API Route
 */
export async function signIn(email: string, password: string): Promise<User | null> {
    logAuthStep('Login Init', 'Start', `Email: ${email}`);
    
    // 1. Anropa vår Vercel API Route som sätter HttpOnly Cookies
    const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
        credentials: 'include' 
    });

    const data = await response.json();

    if (!response.ok) {
        logAuthStep('API Login', '200 OK', `Error ${response.status}: ${data.error}`, 'error');
        throw new Error(data.error || 'Inloggningen misslyckades');
    }

    logAuthStep('API Login', 'Success', 'Tokens received from server', 'success');

    // 2. Hydrera klienten med sessionen vi fick tillbaka
    if (data.session) {
        const { error: setSessionError } = await (supabase.auth as any).setSession(data.session);
        if (setSessionError) {
            logAuthStep('Token Sync', 'Success', `Failed: ${setSessionError.message}`, 'warning');
        } else {
            logAuthStep('Token Sync', 'Success', 'Session set in memory', 'success');
        }
    } else {
        logAuthStep('Token Sync', 'Session Data', 'MISSING in response', 'error');
    }

    // 3. Returnera User-objektet
    if (data.user) {
        let user: User | null = null;
        if (data.user.profile) {
            user = mapUser(data.user.profile);
            logAuthStep('User Profile', 'From API', `Found: ${user.name}`, 'success');
        } else {
            user = await fetchUserProfile(data.user.id);
            logAuthStep('User Profile', 'From DB', user ? 'Found' : 'Not Found', user ? 'success' : 'warning');
        }
        
        if (!user) {
            logAuthStep('User Profile', 'Repair', 'Attempting repair...', 'warning');
            user = await ensureUserProfile(data.user);
        }
        return user;
    }

    throw new Error("Ingen användare hittades efter inloggning.");
}

/** Supabase svarar så här när inloggningslänken gäller en adress utan konto. */
export const isUnknownUserError = (error: { message?: string; code?: string } | null | undefined): boolean =>
    !!error && (error.code === 'otp_disabled' || /signups? not allowed/i.test(error.message || ''));

export async function sendMagicLink(email: string) {
    // Client-side handling via AuthContext hash detection
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await (supabase.auth as any).signInWithOtp({
        email,
        options: {
            emailRedirectTo: redirectUrl,
            // Konton skapas bara av admin eller via registreringen, aldrig av en inloggningslänk.
            shouldCreateUser: false,
        }
    });

    // Okänd adress: säg inget om det, så går det inte att testa vilka adresser som har konto.
    if (error && !isUnknownUserError(error)) throw error;
}

export async function signOut() {
    logAuthStep('Logout', 'Start', 'Clearing session...');
    await (supabase.auth as any).signOut(); 
    localStorage.clear(); 
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
}

export async function resetPasswordForEmail(email: string) {
    // Redirect to Dashboard Settings where user can change password
    const redirectUrl = `${window.location.origin}/#/dashboard?tab=settings`;
    
    const { error } = await (supabase.auth as any).resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    if (error) throw error;
}

export async function updateUserPassword(newPassword: string) {
    const { error } = await (supabase.auth as any).updateUser({ password: newPassword });
    if (error) throw error;
}
